/**
 * Theme Configuration
 * 
 * Central theme configuration for React Native Paper
 * - Platform-specific font loading (web uses 'Noto Sans', native uses .ttf files)
 * - Material Design 3 typography scale with custom font config
 * - Dynamic theme color support (setDynamicTheme updates primary color)
 * - Exports font families, font config, and theme object
 * 
 * @module theme
 */
import { Platform } from 'react-native';
import { configureFonts, MD3LightTheme } from 'react-native-paper';

/** Platform detection flag */
export const isWeb = Platform.OS === 'web';
/** Base font family (Noto Sans) */
export const baseFont = isWeb ? 'Noto Sans' : 'NotoSans-Regular';
/** Medium weight font */
export const mediumFont = isWeb ? 'Noto Sans' : 'NotoSans-Medium';
/** Semi-bold weight font */
export const semiBoldFont = isWeb ? 'Noto Sans' : 'NotoSans-SemiBold';
/** Bold weight font */
export const boldFont = isWeb ? 'Noto Sans' : 'NotoSans-Bold';
/** Light weight font */
export const lightFont = isWeb ? 'Noto Sans' : 'NotoSans-Light';
/** Thin weight font */
export const thinFont = isWeb ? 'Noto Sans' : 'NotoSans-Thin';

/**
 * Material Design 3 typography configuration
 * Defines font family, size, weight, letter spacing, and line height for each variant
 */
export const fontConfig = {
  displaySmall: { fontFamily: baseFont, fontSize: 36, fontWeight: '400', letterSpacing: 0, lineHeight: 44 },
  displayMedium: { fontFamily: baseFont, fontSize: 45, fontWeight: '400', letterSpacing: 0, lineHeight: 52 },
  displayLarge: { fontFamily: baseFont, fontSize: 57, fontWeight: '400', letterSpacing: 0, lineHeight: 64 },

  headlineSmall: { fontFamily: baseFont, fontSize: 24, fontWeight: '400', letterSpacing: 0, lineHeight: 32 },
  headlineMedium: { fontFamily: baseFont, fontSize: 28, fontWeight: '400', letterSpacing: 0, lineHeight: 36 },
  headlineLarge: { fontFamily: baseFont, fontSize: 32, fontWeight: '400', letterSpacing: 0, lineHeight: 40 },

  titleSmall: { fontFamily: mediumFont, fontSize: 14, fontWeight: '500', letterSpacing: 0.1, lineHeight: 20 },
  titleMedium: { fontFamily: mediumFont, fontSize: 16, fontWeight: '500', letterSpacing: 0.15, lineHeight: 24 },
  titleLarge: { fontFamily: baseFont, fontSize: 22, fontWeight: '400', letterSpacing: 0, lineHeight: 28 },
  titleLargeBold: { fontFamily: boldFont, fontSize: 20, fontWeight: '600', letterSpacing: 0, lineHeight: 28 },

  labelSmall: { fontFamily: mediumFont, fontSize: 11, fontWeight: '500', letterSpacing: 0.5, lineHeight: 16 },
  labelMedium: { fontFamily: mediumFont, fontSize: 12, fontWeight: '500', letterSpacing: 0.5, lineHeight: 16 },
  labelLarge: { fontFamily: mediumFont, fontSize: 14, fontWeight: '500', letterSpacing: 0.1, lineHeight: 20 },

  bodySmall: { fontFamily: baseFont, fontSize: 12, fontWeight: '400', letterSpacing: 0.4, lineHeight: 16 },
  bodyMedium: { fontFamily: baseFont, fontSize: 14, fontWeight: '400', letterSpacing: 0.25, lineHeight: 20 },
  bodyLarge: { fontFamily: baseFont, fontSize: 16, fontWeight: '400', letterSpacing: 0.15, lineHeight: 24 },

  default: { fontFamily: baseFont, fontWeight: '400', letterSpacing: 0 },

  textLight: { fontFamily: lightFont, fontSize: 14, fontWeight: '300', letterSpacing: 0.4, lineHeight: 18 },
  textThin: { fontFamily: thinFont, fontSize: 14, fontWeight: '100', letterSpacing: 0.4, lineHeight: 18 },
  textBold: { fontFamily: boldFont, fontSize: 14, fontWeight: '700', letterSpacing: 0.4, lineHeight: 18 },
};

/**
 * Main theme object for React Native Paper
 * Extends Material Design 3 Light Theme with custom colors and fonts
 */
export let theme = {
  ...MD3LightTheme,
  colors: {
    ...MD3LightTheme.colors,
    outline: '#dddddd',
  },
  fonts: configureFonts({ config: fontConfig }),
};

// console.log('theme', theme);

/**
 * Updates theme with dynamic primary color from API
 * @param {Object} newTheme - Theme object
 * @param {string} [newTheme.theme_color] - Hex color code for primary brand color
 */
export const setDynamicTheme = (newTheme) => {
  theme = {
    ...theme,
    colors: {
      ...theme.colors,
      primary: newTheme?.theme_color || theme.colors.primary,
      outline: '#dddddd',
    },
  };
};

/**
 * Gets the current primary theme color
 * @returns {string} Primary color hex code
 */
export const getPrimaryColor = () => {
  return theme.colors.primary;
};

/**
 * Generates safe area insets for navigation bar (Android only)
 * @param {Object} insets - SafeAreaInsets object
 * @param {number} insets.bottom - Bottom inset value
 * @returns {Object} Style object with marginBottom
 */
export const safeAreaNavigationBar = (insets) => {
  return { marginBottom: Platform.OS === 'android' ? insets.bottom : 0 };
};

/**
 * Common modal container style
 * Positions modal at bottom with margin
 */
export const modalConatiner = {
  margin: 5,
  marginBottom: 16,
  flex: 1,
  justifyContent: 'flex-end',
};

/**
 * Modal header style with horizontal layout
 * Includes top rounded corners
 */
export const modalHeader = {
  position: 'relative',
  justifyContent: 'space-between',
  flexDirection: 'row',
  alignItems: 'center',
  paddingHorizontal: 12,
  paddingVertical: 8,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
};

/**
 * Circular close button style for modals
 * Positioned at top-right corner
 */
export const modalCloseIcon = {
  position: 'absolute',
  top: 10, // Adjust as needed
  right: 10, // Adjust as needed
  backgroundColor: '#FFF',
  borderRadius: 20, // Makes it circular if height and width are equal
  width: 32,
  height: 32,
  justifyContent: 'center',
  alignItems: 'center',
};

/**
 * Platform-specific flex container style
 * Uses flexGrow for web, flex for mobile
 */
export const flexContainer = {
      ...(Platform.OS === 'web'
        ? { flexGrow: 1 } // Web
        : { flex: 1 }),    // Mobile
}